import { Box, Card, LinearProgress, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { salesApi } from "../client.js";

/**
 * The contact-harvester's status — shown on the Status tab when the agent is in
 * contacts mode (instead of the lead-agent hero/quality/directives). At-a-glance
 * pool counts + target-matrix coverage. The live "what it's doing" activity feed
 * moved to the Brain tab (2026-06-19) — Status is the metrics/health glance,
 * Brain is the agent's live consciousness.
 */
export function HarvesterStatus(): JSX.Element {
  const stats = useQuery({
    queryKey: ["sales", "harvest", "stats"],
    queryFn: salesApi.getHarvestStats,
    refetchInterval: 15_000,
  });
  const strategy = useQuery({
    queryKey: ["sales", "harvest", "strategy"],
    queryFn: salesApi.getHarvestStrategy,
    refetchInterval: 60_000,
  });

  const s = stats.data;
  const cells = (strategy.data?.campaigns ?? []).reduce(
    (acc, c) => {
      acc.total += c.matrix_cells;
      acc.done += c.cells_done;
      acc.pending += c.cells_pending;
      return acc;
    },
    { total: 0, done: 0, pending: 0 },
  );
  const pct = cells.total > 0 ? Math.round((cells.done / cells.total) * 100) : 0;
  const verified = s?.contacts_by_email_status?.valid ?? 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Tile label="Organisations" value={s?.total_orgs} />
        <Tile label="Contacts" value={s?.total_contacts} />
        <Tile label="With email" value={s?.contacts_with_email} />
        <Tile label="Verified ✓" value={verified} />
        <Tile label="Leads created" value={s?.leads_created} />
      </Stack>

      {cells.total > 0 && (
        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Target coverage</Typography>
            <Typography variant="caption" color="text.secondary">
              {cells.done}/{cells.total} industry × region cells swept · {cells.pending} queued
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={pct} sx={{ mt: 1, borderRadius: 1, height: 6 }} />
        </Card>
      )}
    </Box>
  );
}

function Tile({ label, value }: { label: string; value: number | undefined }): JSX.Element {
  return (
    <Card variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 130 }}>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>{value?.toLocaleString() ?? "—"}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Card>
  );
}
